import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { laborVarianceReport } from "@/lib/labor-variance.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Gauge } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/labor/")({ component: LaborOverview });

const RANGES = [1, 7, 14, 30] as const;

function fmtHours(secs: number) {
  return (secs / 3600).toFixed(1) + "h";
}

function LaborOverview() {
  const { tenantId, facilityId } = useScope();
  const varianceFn = useServerFn(laborVarianceReport);
  const [days, setDays] = useState<number>(7);

  const { data: variance } = useQuery({
    queryKey: ["labor-variance", tenantId, facilityId, days],
    enabled: !!tenantId,
    queryFn: () => varianceFn({ data: { tenantId: tenantId!, facilityId: facilityId ?? null, days } }),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title="Labor & Productivity"
        description="Variance against engineered standards. CairnOS does not rank workers — it surfaces process drift."
        meta={
          <Link to="/app/labor/incentives" className="text-xs text-primary underline">
            Incentive payouts →
          </Link>
        }
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <Tabs value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <TabsList>
            {RANGES.map((d) => (
              <TabsTrigger key={d} value={String(d)}>
                {d === 1 ? "Today" : `${d}d`}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <Card>
          <CardHeader className="flex flex-row items-center gap-2 pb-3">
            <Gauge className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">Variance vs Standard</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Activity</TableHead>
                    <TableHead className="text-right">Events</TableHead>
                    <TableHead className="text-right">Actual</TableHead>
                    <TableHead className="text-right">Standard</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                    <TableHead className="text-right">Coverage</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!variance && (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Loading…</TableCell></TableRow>
                  )}
                  {variance && variance.activities.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No completed labor events in this window.</TableCell></TableRow>
                  )}
                  {variance?.activities.map((a) => (
                    <TableRow key={a.activity}>
                      <TableCell className="text-sm font-medium">{a.activity}</TableCell>
                      <TableCell className="text-right tabular-nums">{a.events}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtHours(a.actualSecs)}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {a.expectedSecs > 0 ? fmtHours(a.expectedSecs) : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {a.variancePct == null
                          ? <span className="text-muted-foreground">—</span>
                          : <Badge variant={a.variancePct > 10 ? "destructive" : a.variancePct < -10 ? "default" : "outline"}>
                              {a.variancePct > 0 ? "+" : ""}{a.variancePct}%
                            </Badge>}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground text-xs">{a.coverage}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
